import {SupportedLocales} from '#types/locale.js';

export const governanceRulesLanguage = {
  [SupportedLocales.EN]: {
    pageTitle: 'Prioritization & Rules',
    pageDescription: 'Define organization-wide governance rules.',
    createRule: 'Create rule',
    audience: 'Audience',
    duration: 'Duration',
    block: 'Block',
    requestApproval: 'Request approval',
    create: {
      dialogTitle: 'Create rule',
      scopeStep: 'Scope',
      typeStep: 'Rule type',
      configureStep: 'Configure',
      scopeTitle: 'Choose a governed resource',
      scopeDescription: 'What does this rule govern?',
      audienceDescription: 'Set a rule for Audience exports.',
      typeTitle: 'Choose a rule type',
      typeDescription: 'What should this rule control?',
      durationDescription:
        'Limit how long a recurring export can keep running.',
      configureTitle: 'Configure the rule',
      nameLabel: 'Rule name',
      namePlaceholder: 'Audience duration limit',
      durationLabel: 'Maximum duration',
      durationHint:
        'Counted from the first run. Anything scheduled beyond this is caught before it goes live.',
      days: 'days',
      decreaseDuration: 'Decrease maximum duration by one day',
      increaseDuration: 'Increase maximum duration by one day',
      thresholdLabel: 'If the threshold is exceeded',
      blockDescription:
        'Stop it outright. The user sees why and can adjust before saving.',
      approvalDescription:
        'Route it to an approver. It waits in Approvals until a decision.',
      back: 'Back',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving',
      saved: 'Governance rule created.',
      saveError: 'The rule could not be saved. Your values are retained.',
      discardChanges: 'Discard your unsaved changes?',
      validation: {
        scopeRequired: 'Select Audience.',
        typeRequired: 'Select Duration.',
        nameRequired: 'Enter a rule name.',
        nameTooLong: 'Rule name must be 255 characters or fewer.',
        durationRequired: 'Enter a maximum duration.',
        durationWhole: 'Maximum duration must be a whole number.',
        durationRange:
          'Maximum duration must be between 1 and 2,147,483,647 days.',
      },
    },
    table: {
      title: 'Governance rules',
      search: 'Search rule names',
      clearFilters: 'Clear filters',
      emptyTitle: 'Create your first rule',
      emptyDescription:
        'Block or require approval when audiences and journeys fall outside your organization’s policies.',
      noMatches: 'No rules match these filters.',
      noFilterOptions: 'No filter options available.',
      loading: 'Loading governance rules',
      loadError: 'Governance rules could not be loaded.',
      retry: 'Retry',
      columns: {
        id: 'ID',
        rule: 'Rule',
        scope: 'Scope',
        condition: 'Condition',
        action: 'Action',
        status: 'Status',
      },
      values: {
        audience: 'Audience',
        journey: 'Journey',
        block: 'Block',
        requireApproval: 'Require approval',
        configured: 'Configured',
      },
      conditions: {
        audienceMaximumDuration: (days: number) =>
          `Audience export duration exceeds ${days} days`,
        journeyMaximumDuration: (days: number) =>
          `Journey scheduled run duration exceeds ${days} days`,
      },
    },
  },
  [SupportedLocales.ES]: {
    pageTitle: 'Priorización y reglas',
    pageDescription: 'Define reglas de gobierno para toda la organización.',
    createRule: 'Crear regla',
    audience: 'Audiencia',
    duration: 'Duración',
    block: 'Bloquear',
    requestApproval: 'Solicitar aprobación',
    create: {
      dialogTitle: 'Crear regla',
      scopeStep: 'Alcance',
      typeStep: 'Tipo de regla',
      configureStep: 'Configurar',
      scopeTitle: 'Elige un recurso gobernado',
      scopeDescription: '¿Qué debe gobernar esta regla?',
      audienceDescription:
        'Establece una regla para las exportaciones de audiencias.',
      typeTitle: 'Elige un tipo de regla',
      typeDescription: '¿Qué debe controlar esta regla?',
      durationDescription:
        'Limita el tiempo que puede ejecutarse una exportación recurrente.',
      configureTitle: 'Configura la regla',
      nameLabel: 'Nombre de la regla',
      namePlaceholder: 'Límite de duración de audiencia',
      durationLabel: 'Duración máxima',
      durationHint:
        'Se cuenta desde la primera ejecución. Todo lo programado después se bloquea antes de publicarse.',
      days: 'días',
      decreaseDuration: 'Reducir la duración máxima en un día',
      increaseDuration: 'Aumentar la duración máxima en un día',
      thresholdLabel: 'Si se supera el límite',
      blockDescription:
        'Detén la operación. El usuario verá el motivo y podrá ajustarla antes de guardar.',
      approvalDescription:
        'Envíala a una persona aprobadora. Esperará en Aprobaciones hasta que se tome una decisión.',
      back: 'Atrás',
      cancel: 'Cancelar',
      save: 'Guardar',
      saving: 'Guardando',
      saved: 'Regla de gobierno creada.',
      saveError:
        'No se pudo guardar la regla. Se conservaron los valores introducidos.',
      discardChanges: '¿Quieres descartar los cambios sin guardar?',
      validation: {
        scopeRequired: 'Selecciona Audiencia.',
        typeRequired: 'Selecciona Duración.',
        nameRequired: 'Introduce un nombre para la regla.',
        nameTooLong: 'El nombre debe tener 255 caracteres o menos.',
        durationRequired: 'Introduce una duración máxima.',
        durationWhole: 'La duración máxima debe ser un número entero.',
        durationRange:
          'La duración máxima debe estar entre 1 y 2.147.483.647 días.',
      },
    },
    table: {
      title: 'Reglas de gobernanza',
      search: 'Buscar nombres de reglas',
      clearFilters: 'Borrar filtros',
      emptyTitle: 'Crea tu primera regla',
      emptyDescription:
        'Bloquea o exige aprobación cuando las audiencias y los recorridos incumplan las políticas de tu organización.',
      noMatches: 'Ninguna regla coincide con estos filtros.',
      noFilterOptions: 'No hay opciones de filtro disponibles.',
      loading: 'Cargando reglas de gobernanza',
      loadError: 'No se pudieron cargar las reglas de gobernanza.',
      retry: 'Reintentar',
      columns: {
        id: 'ID',
        rule: 'Regla',
        scope: 'Ámbito',
        condition: 'Condición',
        action: 'Acción',
        status: 'Estado',
      },
      values: {
        audience: 'Audiencia',
        journey: 'Recorrido',
        block: 'Bloquear',
        requireApproval: 'Requerir aprobación',
        configured: 'Configurada',
      },
      conditions: {
        audienceMaximumDuration: (days: number) =>
          `La duración de exportación de la audiencia supera los ${days} días`,
        journeyMaximumDuration: (days: number) =>
          `La duración de la ejecución programada del recorrido supera los ${days} días`,
      },
    },
  },
};
